import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, MessageSquare } from 'lucide-react';
import { PA_API, paErr, fmtDT, Panel, inputCls, btnPrimary, Pill } from './shared';

export default function MessagesTab({ onChanged, initialCustomer }) {
  const [threads, setThreads] = useState(null);
  const [cid, setCid] = useState(initialCustomer || null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadThreads = useCallback(() => axios.get(`${PA_API}/threads`).then((r) => { setThreads(r.data); if (!cid && r.data.length) setCid(r.data[0].customer_id); }).catch((e) => toast.error(paErr(e))), [cid]);
  const loadThread = useCallback(() => cid && axios.get(`${PA_API}/threads/${cid}`).then((r) => { setMsgs(r.data); onChanged(); }).catch(() => {}), [cid, onChanged]);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { loadThread(); const t = setInterval(loadThread, 20000); return () => clearInterval(t); }, [loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !cid) return;
    setBusy(true);
    try {
      const r = await axios.post(`${PA_API}/threads/${cid}/messages`, { text: text.trim() });
      setMsgs((m) => [...m, r.data]); setText(''); loadThreads();
    } catch (err) { toast.error(paErr(err)); } finally { setBusy(false); }
  };
  const cur = threads?.find((t) => t.customer_id === cid);

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-3" style={{ minHeight: '60vh' }} data-testid="pa-messages">
      <Panel className="p-2 overflow-y-auto">
        {!threads ? <div className="text-slate-500 text-sm p-2">Loading…</div> : threads.length === 0 ? <div className="text-slate-500 text-sm p-3">No portal customers yet.</div> : threads.map((t) => (
          <button type="button" key={t.customer_id} onClick={() => setCid(t.customer_id)} data-testid={`pa-thread-${t.customer_id}`}
            className={`w-full text-left px-3 py-2.5 rounded-lg mb-1 ${cid === t.customer_id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-100'}`}>
            <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-slate-900 truncate">{t.customer_name}</span>{t.unread > 0 && <Pill tone="amber">{t.unread}</Pill>}</div>
            <div className="text-xs text-slate-500 truncate">{t.last ? `${t.last.sender_type === 'staff' ? 'You' : t.last.sender_name}: ${t.last.text}` : 'No messages yet'}</div>
            {t.last && <div className="text-[10px] text-slate-500 font-mono">{fmtDT(t.last.created_at)}</div>}
          </button>))}
      </Panel>
      <Panel className="flex flex-col" testId="pa-thread-panel">
        {!cur ? <div className="flex-1 flex items-center justify-center text-slate-500 text-sm"><MessageSquare className="w-5 h-5 mr-2" /> Pick a customer</div> : (
          <>
            <div className="px-4 py-3 border-b border-slate-200 text-slate-900 font-semibold">{cur.customer_name}</div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" style={{ maxHeight: '55vh' }} data-testid="pa-thread-messages">
              {msgs.length === 0 ? <div className="text-sm text-slate-500 text-center py-8">No messages yet — say hello.</div> : msgs.map((m) => {
                const staff = m.sender_type === 'staff';
                return (
                  <div key={m.id} className={`flex ${staff ? 'justify-end' : 'justify-start'}`} data-testid={`pa-msg-${m.id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${staff ? 'bg-blue-600 text-white rounded-br-md' : 'bg-slate-100 text-slate-800 rounded-bl-md'}`}>
                      <div className={`text-[11px] font-bold ${staff ? 'text-blue-800' : 'text-amber-700'}`}>{m.sender_name}{staff ? ' · 365' : ` · ${m.customer_name}`}</div>
                      <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                      <div className={`text-[10px] font-mono mt-0.5 ${staff ? 'text-blue-800' : 'text-slate-500'}`}>{fmtDT(m.created_at)}</div>
                    </div>
                  </div>);
              })}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="border-t border-slate-200 p-3 space-y-2">
              <textarea className={`${inputCls} resize-none`} rows={2} placeholder={`Reply to ${cur.customer_name}…`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="pa-reply-input" />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500" data-testid="pa-reply-digest-note">No email per message — unread messages go out as one summary at 4:30 pm (Mon–Fri) to everyone with a login for {cur.customer_name}.</span>
                <button type="submit" className={btnPrimary} disabled={busy || !text.trim()} data-testid="pa-reply-send"><Send className="w-4 h-4" /> Send</button>
              </div>
            </form>
          </>)}
      </Panel>
    </div>
  );
}
